import axios from 'axios';

/**
 * 🔐 SECURITY: Axios instance configured for httpOnly cookies
 * This ensures cookies are automatically sent and received with every request
 */

const axiosInstance = axios.create({
  // ✅ Empty baseURL - use proxy from setupProxy.js
  baseURL: '',
  withCredentials: true, // ✅ Always send and receive cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for debugging
axiosInstance.interceptors.request.use(
  (config) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('📡 Request:', config.method?.toUpperCase(), config.url);
    }
    return config;
  },
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

// Track whether we're already attempting to refresh to avoid infinite loops
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  isRefreshing = false;
  failedQueue = [];
};

// Add response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Response:', response.status, response.config.url);
    }
    return response;
  },
  async (error) => {
    // If 401, try to refresh the token
    if (error.response?.status === 401 && error.config && !error.config._retry) {
      const originalRequest = error.config;

      // Mark request as retrying to avoid infinite loops
      originalRequest._retry = true;

      // Avoid infinite refresh loops
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          console.warn('🔐 Token expired (401) - attempting to refresh...');
          
          // Use fetch for refresh to bypass this interceptor
          const refreshResponse = await fetch('/api/auth/refresh', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include', // Send httpOnly cookies
          });

          if (refreshResponse.status === 200) {
            console.log('✅ Token refreshed successfully');
            processQueue(null);
            
            // Retry original request with new token (in cookie now)
            return axiosInstance(originalRequest);
          } else {
            throw new Error(`Refresh failed with status ${refreshResponse.status}`);
          }
        } catch (refreshError: any) {
          console.error('❌ Token refresh failed:', refreshError.message);
          processQueue(refreshError, null);
          
          // Call logout endpoint to clear cookies
          try {
            await fetch('/api/auth/logout', {
              method: 'POST',
              credentials: 'include'
            });
            console.log('🔓 Cookies cleared - redirecting to login');
          } catch (logoutErr) {
            console.error('Logout error:', logoutErr);
          }
          
          // Redirect to login page with absolute URL
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
          
          return Promise.reject(refreshError);
        }
      } else {
        // If already refreshing, queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ 
            resolve: () => {
              // Retry with new token
              axiosInstance(originalRequest).then(resolve).catch(reject);
            }, 
            reject 
          });
        });
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
